import { useState, useCallback, useRef, useEffect } from "react";
import { HttpStatus, enumsPaginatedSelect } from "utils/enums";
import { isNullOrUndefined } from "utils/validations";

const defaultExtractItems = (response) => response.data?.items || [];
const defaultExtractTotalPages = (response) => response.data?.pages ?? null;

/**
 * Hook genérico para paginación en react-select con scroll loading, búsqueda con debounce y caché por término de búsqueda.
 * @param {Object} config
 * @param {Function} config.fetchFn - (page, perPage, search) => Promise (llamada al API)
 * @param {Function} config.formatFn - (item) => { value, label }
 * @param {Function} [config.extractItems] - (response) => items[] (extrae los items de la respuesta, default: response.data.items)
 * @param {Function} [config.extractTotalPages] - (response) => number|null (extrae el total de páginas de la respuesta, default: response.data.pages)
 * @param {Function} [config.filterFn] - (item) => boolean (filtra items antes de formatear, no afecta hasMore)
 * @param {number} [config.perPage=enumsPaginatedSelect.DEFAULT_PER_PAGE]
 * @param {number} [config.debounceDelay=enumsPaginatedSelect.DEFAULT_DEBOUNCE_DELAY]
 */
export default function usePaginatedSelect({
    fetchFn,
    formatFn,
    extractItems = defaultExtractItems,
    extractTotalPages = defaultExtractTotalPages,
    filterFn = null,
    perPage = enumsPaginatedSelect.DEFAULT_PER_PAGE,
    debounceDelay = enumsPaginatedSelect.DEFAULT_DEBOUNCE_DELAY,
}) {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);
  const searchRef = useRef("");
  const debounceRef = useRef(null);
  const cacheRef = useRef(new Map());

    const fetchFnRef = useRef(fetchFn);
    const formatFnRef = useRef(formatFn);
    const extractItemsRef = useRef(extractItems);
    const extractTotalPagesRef = useRef(extractTotalPages);
    const filterFnRef = useRef(filterFn);

    /**
     * Updates the refs for fetchFn, formatFn, extractItems,
     * and filterFn whenever they change to ensure the latest versions are used in callbacks.
     */
    useEffect(() => {
        fetchFnRef.current = fetchFn;
        formatFnRef.current = formatFn;
        extractItemsRef.current = extractItems;
        extractTotalPagesRef.current = extractTotalPages;
        filterFnRef.current = filterFn;
    });

  /**
   * Cleans up any pending debounce timers when the component using this hook unmounts to prevent memory leaks.
   */
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

    /** 
     * Loads options for the combo box based on the given page number, search term, and whether to append to existing options.
     * It sets the loading state, calls the fetch function, processes the response, and updates the options and pagination state.
     * @param {number} page - The page number to load.
     * @param {string} search - The search term to filter options.
     * @param {boolean} append - Whether to append the new options to existing ones or replace them.
     * @returns {Promise<void>}
     */
    const loadOptions = useCallback(async (page, search, append) => {
        setIsLoading(true);
        try {
            const response = await fetchFnRef.current(page, perPage, search);
            const items = extractItemsRef.current(response);
            if (isNullOrUndefined(items) === false) {
                const totalPages = extractTotalPagesRef.current(response);
                const hasMoreItems = totalPages !== null
                    ? page < totalPages
                    : items.length === perPage;
                setHasMore(hasMoreItems);
                let filtered = items;
                if (filterFnRef.current) {
                    filtered = items.filter(filterFnRef.current);
                }
                const formatted = filtered.map(formatFnRef.current);
                setOptions((prev) => {
                    let newOptions = formatted;
                    if (append) {
                        newOptions = [...prev, ...formatted];
                    }
                    cacheRef.current.set(search, {
                        options: newOptions,
                        page,
                        hasMore: hasMoreItems,
                    });
                    return newOptions;
                });
                pageRef.current = page;
                searchRef.current = search;
            }
        } catch (error) {
            if (error?.response?.status === HttpStatus.UNAUTHORIZED) return;
        } finally {
            setIsLoading(false);
        }
    }, [perPage]);

  /**
   * Tries to restore options from cache based on the search term. If cached data is found, it updates the options and pagination state accordingly.
   * @param {string} search - The search term to look up in the cache.
   * @returns {boolean} - Returns true if cached data was found and restored, false otherwise.
   */
  const restoreFromCache = useCallback((search) => {
    const cached = cacheRef.current.get(search);
    if (isNullOrUndefined(cached) === false) {
      setOptions(cached.options);
      pageRef.current = cached.page;
      searchRef.current = search;
      setHasMore(cached.hasMore);
      return true;
    }
    return false;
  }, []);

  /** Loads the initial options when the component mounts or when itemLink/hide changes.
   * Resets the paginated state and fetches the first page of items.
   * Skips fetching if the component is not in preview mode and is hidden.
   * @param {string} search - The search term to filter options when loading the initial set.
   * @returns {void}
   */
  const loadInitial = useCallback(
    (search = "") => {
      if (restoreFromCache(search)) return;
      return loadOptions(1, search, false);
    },
    [loadOptions, restoreFromCache],
  );

  /**
   * Handles the input change event for the combo box, triggering a debounced search for options based on user input.
   * When the user types in the combo box, this function is called with the new input value and the action type.
   * If the action type is "input-change", it calls the debounced loadInitial function to fetch options matching the input.
   * @param {string} search - The current value of the input field in the combo box.
   * @returns {void}
   */
  const loadInitialDebounced = useCallback(
    (search = "") => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        if (!restoreFromCache(search)) {
          loadOptions(1, search, false);
        }
      }, debounceDelay);
    },
    [loadOptions, debounceDelay, restoreFromCache],
  );

  /**
   * Loads more options when the user scrolls to the bottom of the combo box.
   * It checks if there are more options to load and if a loading operation is not already
   * in progress before fetching the next page of options.
   */
  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    loadOptions(pageRef.current + 1, searchRef.current, true);
  }, [hasMore, isLoading, loadOptions]);

  /**
   * Resets the paginated state and clears the cache.
   * Cancels any pending debounced operations.
   */
  const reset = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setOptions([]);
    pageRef.current = 1;
    searchRef.current = "";
    setHasMore(true);
    cacheRef.current.clear();
  }, []);

  /**
   * Resets the search term and clears any pending debounce timers.
   * This is typically called when the combo box menu is closed to ensure that the next time it is opened,
   * it starts with a fresh search state.
   * @returns {void}
   */
  const resetSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    searchRef.current = "";
  }, []);

  return {
    options,
    isLoading,
    hasMore,
    loadInitial,
    loadInitialDebounced,
    loadMore,
    reset,
    resetSearch,
  };
}
